<script setup lang="ts">
useSeoMeta({
  title: 'About — Shakir Abdulrahim',
  ogTitle: 'About — Shakir Abdulrahim',
  description: 'The story behind the code. Full-stack web developer and hardware tinkerer based in Saudi Arabia.',
  ogDescription: 'The story behind the code. Full-stack web developer and hardware tinkerer based in Saudi Arabia.',
})

const skillGroups = [
  {
    name: 'Web',
    color: 'var(--color-amber)',
    skills: ['JavaScript', 'TypeScript', 'Node.js', 'Nuxt/Vue', 'MongoDB', 'Directus', 'PostgreSQL', 'HTML & CSS', 'Git'],
  },
  {
    name: 'Hardware / IoT',
    color: 'var(--color-steel-blue)',
    skills: ['ESPHome', 'Home Assistant', 'CH32V003', 'RISC-V', 'PCB Design', 'Sensor Integration', '3D Printing', '3D Modeling'],
  },
  {
    name: 'DevOps',
    color: '#a78bfa',
    skills: ['Dokploy', 'Docker', 'CI/CD', 'SSL/Certs', 'Reverse Proxies', 'Nginx'],
  },
  {
    name: 'Emerging',
    color: '#34d399',
    skills: ['LLM Architecture', 'RAG Systems', 'Generative AI', 'LangChain', 'AI Agents'],
  },
]

const timeline = [
  { year: '2015', title: 'Started Coding', description: 'First steps into programming and web development. The beginning of a decade-long journey.' },
  { year: '2016', title: 'GitHub & Open Source', description: 'Joined GitHub, started exploring Vue.js, JavaScript ecosystems, and contributing to open source.' },
  { year: '2018', title: 'Bots & Automation', description: 'Built Telegram bots and automation tools, diving deeper into Node.js and server-side development.' },
  { year: '2019', title: 'B.IT — Alofoug College, Khartoum', description: 'Started Bachelor of Information Technology. Built Wathiq API and expanded into Vue.js applications.' },
  { year: '2020', title: 'Full-Stack Growth', description: 'Built multiple Vue/Nuxt projects — LegalEditor, GoBrew, and various web apps. Started building with CMS platforms.' },
  { year: '2021', title: 'Wethaq Advocates & Legal Consultants', description: 'Full-stack developer in Riyadh — built the Wathiqa platform, legal tools, and multiple web applications for Legal Tech.' },
  { year: '2022', title: 'Taqnien & Gov-Scale Systems', description: 'Developed Ahkam, Violations, Tanzemat, Eltizam, and ACOST systems — Vue/Nuxt frontends with CMS backends at scale.' },
  { year: '2023', title: 'AI & LLMs', description: 'Built GPT document chat, LangChain experiments, WhatsApp AI shopper. Completed DeepLearning.AI GenAI course and Mini-MBA.' },
  { year: '2024', title: 'Hardware + Open Source', description: 'Published CH32V003 RISC-V libraries, ESP32 WebSocket projects, and expanded into IoT with ESPHome components.' },
  { year: '2025', title: 'AI Agents & IoT', description: 'Building insurance GPT, Motalbat platform, Lexora bot, and IoT MCP servers. Bridging AI with embedded systems.' },
]
</script>

<template>
  <div>
    <!-- Hero -->
    <section class="py-24">
      <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <p class="mb-4 text-sm font-medium tracking-widest text-[var(--color-amber)] uppercase">
          About
        </p>
        <h1 class="mb-8 text-4xl font-bold text-[var(--color-text)] sm:text-5xl">
          The story behind the code
        </h1>
        <div class="space-y-6 text-lg leading-relaxed text-[var(--color-text-muted)]">
          <p>
            I'm Shakir Abdulrahim — a full-stack web developer based in Saudi Arabia with nearly a decade of building things that matter, from Legal Tech platforms to bare-metal microcontrollers.
          </p>
          <p>
            I started coding in 2015 and never stopped. After a B.IT from Alofoug College in Khartoum, I moved to Riyadh where I built the Wathiqa Legal Tech platform at Wethaq Advocates, then developed government-scale systems at Taqnien — Ahkam, Violations tracking, Tanzemat, and more. Over 366 repositories on GitHub later, I've shipped everything from Telegram bots to insurance AI platforms.
          </p>
          <p>
            Along the way, code on a screen was never quite enough. I found myself writing C libraries for RISC-V chips, building ESP32 WebSocket controllers, creating IoT MCP servers, and tinkering with 3D printing and modeling.
          </p>
          <p>
            Now I'm bridging AI with everything — building GPT-powered document chat, LangChain applications, AI shopping bots, and exploring how intelligent agents can augment the kind of engineering I've been doing for a decade.
          </p>
        </div>
      </div>
    </section>

    <!-- Skills -->
    <section class="border-t border-[var(--color-border)] bg-[var(--color-card-bg)] py-24">
      <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2 class="mb-12 text-center text-3xl font-bold text-[var(--color-text)]">
          Skills & Expertise
        </h2>

        <div class="grid gap-8 md:grid-cols-2">
          <div
            v-for="group in skillGroups"
            :key="group.name"
            class="rounded-xl border border-[var(--color-card-border)] bg-[var(--color-surface)] p-6"
          >
            <h3
              class="mb-4 text-lg font-semibold"
              :style="{ color: group.color }"
            >
              {{ group.name }}
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="skill in group.skills"
                :key="skill"
                class="rounded-md bg-[var(--color-surface-hover)] px-3 py-1.5 text-sm text-[var(--color-text-muted)]"
              >
                {{ skill }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Philosophy -->
    <section class="py-24">
      <div class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <blockquote class="text-3xl font-bold italic text-[var(--color-amber)] sm:text-4xl">
          "Stay hungry. Stay foolish."
        </blockquote>
        <p class="mt-6 text-lg text-[var(--color-text-muted)]">
          This isn't just a quote on a wall. It's a commitment to never stop learning, to always be willing to start as a beginner, and to build things that matter — whether it's a government platform serving thousands or a tiny library for a $0.10 microcontroller.
        </p>
      </div>
    </section>

    <!-- Timeline -->
    <section class="border-t border-[var(--color-border)] bg-[var(--color-card-bg)] py-24">
      <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 class="mb-12 text-center text-3xl font-bold text-[var(--color-text)]">
          Journey
        </h2>

        <div class="relative">
          <!-- Vertical line -->
          <div class="absolute left-4 top-0 h-full w-px bg-[var(--color-steel-blue)]/20 md:left-1/2" />

          <div class="space-y-12">
            <div
              v-for="(event, index) in timeline"
              :key="event.year"
              class="relative flex gap-8"
              :class="index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'"
            >
              <!-- Dot -->
              <div class="absolute left-4 top-1 size-2 -translate-x-1/2 rounded-full bg-[var(--color-amber)] md:left-1/2" />

              <!-- Content -->
              <div class="ml-10 md:ml-0 md:w-[calc(50%-2rem)]">
                <span class="text-sm font-medium text-[var(--color-amber)]">{{ event.year }}</span>
                <h3 class="mt-1 text-lg font-semibold text-[var(--color-text)]">
                  {{ event.title }}
                </h3>
                <p class="mt-2 text-sm text-[var(--color-text-muted)]">
                  {{ event.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
